import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeftRight, CalendarDays, Clock3, LogIn, LogOut, Repeat, Trash2 } from "lucide-react";
import { api } from "@/lib/api-client";
import { currency, shortDate } from "@/lib/format";
import { Button } from "@/components/ui/Button";
import type { Booking, BookingRoomStay } from "@/types/domain";
import type { InventoryRoom } from "@/features/inventory/types";
import { roomStayLabel } from "@/features/bookings/room-stay";

export type RoomAction = "check-in" | "check-out" | "change-dates" | "extend" | "change-room" | "exchange" | "remove-room";

const STATUS_CLASS: Record<BookingRoomStay["status"], string> = { CONFIRMED: "confirmed", CHECKED_IN: "checked_in", CHECKED_OUT: "completed", CANCELLED: "cancelled" };
const STATUS_LABEL: Record<BookingRoomStay["status"], string> = { CONFIRMED: "Confirmed", CHECKED_IN: "Checked in", CHECKED_OUT: "Checked out", CANCELLED: "Cancelled" };


const nightsBetween = (from: string, to: string) => Math.max(1, Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86_400_000));

// One card per room on a multi-room booking: each has its own stay, status, amount and actions. Tick rooms to run
// the booking-level buttons on just those (nothing ticked = every eligible room).
export function RoomStaysPanel({ booking, canOperate, prepaidApp = false, selected, busy, onSelect, onAction }: {
  booking: Booking;
  canOperate: boolean;
  /** Prepaid app booking: no date changes or room removal (the server refuses them). */
  prepaidApp?: boolean;
  selected: string[];
  busy: boolean;
  onSelect: (ids: string[]) => void;
  onAction: (action: RoomAction, roomIds: string[]) => void;
}) {
  const rooms = booking.allottedRooms ?? [];
  const live = rooms.filter((room) => room.status !== "CANCELLED");
  const holding = rooms.filter((room) => room.status === "CONFIRMED" || room.status === "CHECKED_IN");
  const toggle = (id: string) => onSelect(selected.includes(id) ? selected.filter((item) => item !== id) : [...selected, id]);

  return (
    <section className="room-stays">
      <header className="room-stays__head">
        <strong>Rooms ({live.length})</strong>
        {canOperate && holding.length > 1 && (
          <span>
            <button type="button" className="link-button" onClick={() => onSelect(holding.map((room) => room.id))}>Select all</button>
            <button type="button" className="link-button" disabled={!selected.length} onClick={() => onSelect([])}>Clear</button>
          </span>
        )}
      </header>
      <div className="room-stays__list">
        {rooms.map((stay) => {
          const isHolding = stay.status === "CONFIRMED" || stay.status === "CHECKED_IN";
          const extras = Number(stay.earlyCheckinCharge ?? 0) + Number(stay.lateCheckoutCharge ?? 0);
          return (
            <article className={`room-stay-card room-stay-card--${STATUS_CLASS[stay.status]}`} key={stay.id}>
              <div className="room-stay-card__main">
                {canOperate && isHolding && <input type="checkbox" aria-label={`Select ${roomStayLabel(stay)}`} checked={selected.includes(stay.id)} onChange={() => toggle(stay.id)} />}
                <div>
                  <strong>{roomStayLabel(stay)}</strong>
                  <small>{shortDate(stay.checkInDate)} → {shortDate(stay.checkOutDate)} · {nightsBetween(stay.checkInDate, stay.checkOutDate)} night(s)</small>
                </div>
                <span className={`status status--${STATUS_CLASS[stay.status]}`}>{STATUS_LABEL[stay.status]}</span>
                <b>{currency.format(Number(stay.amount) + extras)}</b>
              </div>
              {canOperate && isHolding && (
                <div className="room-stay-card__actions">
                  {stay.status === "CONFIRMED" && <button type="button" className="booking-action booking-action--primary" disabled={busy} onClick={() => onAction("check-in", [stay.id])}><LogIn size={14} /> Check in</button>}
                  {stay.status === "CHECKED_IN" && <button type="button" className="booking-action booking-action--primary" disabled={busy} onClick={() => onAction("check-out", [stay.id])}><LogOut size={14} /> Check out</button>}
                  {!prepaidApp && <button type="button" className="booking-action booking-action--neutral" disabled={busy} onClick={() => onAction(stay.status === "CHECKED_IN" ? "extend" : "change-dates", [stay.id])}>
                    {stay.status === "CHECKED_IN" ? <><Clock3 size={14} /> Extend</> : <><CalendarDays size={14} /> Change date</>}
                  </button>}
                  <button type="button" className="booking-action booking-action--neutral" disabled={busy} onClick={() => onAction("change-room", [stay.id])}><Repeat size={14} /> Change room</button>
                  <button type="button" className="booking-action booking-action--neutral" disabled={busy} onClick={() => onAction("exchange", [stay.id])}><ArrowLeftRight size={14} /> Exchange</button>
                  {stay.status === "CONFIRMED" && live.length > 1 && !prepaidApp && <button type="button" className="booking-action booking-action--cancelled" disabled={busy} onClick={() => onAction("remove-room", [stay.id])}><Trash2 size={14} /> Remove</button>}
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

// Adds one more room to a confirmed booking (its own stay, priced at that room's tariff and the booking's rate plan).
export function AddRoomForm({ booking, propertyId, loading, error, onCancel, onSubmit }: {
  booking: Booking;
  propertyId: string;
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (roomId: string) => void;
}) {
  const rooms = useQuery({
    queryKey: ["inventory-rooms", propertyId, booking.checkInDate, booking.checkOutDate],
    queryFn: () => api<InventoryRoom[]>(`/inventory/rooms?propertyId=${propertyId}&date=${booking.checkInDate.slice(0, 10)}&checkOutDate=${booking.checkOutDate.slice(0, 10)}`),
  });
  const [roomId, setRoomId] = useState("");
  const taken = new Set((booking.allottedRooms ?? []).filter((room) => room.status !== "CANCELLED").map((room) => room.roomId));
  const options = (rooms.data ?? []).filter((room) => room.status === "AVAILABLE" && room.isBookable && !taken.has(room.id));

  return (
    <section className="booking-inline-form">
      <h4>Add a room</h4>
      <p className="booking-inline-form__hint">The new room stays for {shortDate(booking.checkInDate)} → {shortDate(booking.checkOutDate)}. Change its dates afterwards if it needs different ones.</p>
      <div className="property-form-grid">
        <label>Room
          <select value={roomId} onChange={(event) => setRoomId(event.target.value)}>
            <option value="">Select an available room</option>
            {options.map((room) => <option key={room.id} value={room.id}>{room.name}{room.roomNumber ? ` - Room ${room.roomNumber}` : ""}</option>)}
          </select>
        </label>
        {!rooms.isLoading && !options.length && <small className="field-error">No other room is free for these dates.</small>}
      </div>
      <div className="form-hint">The booking total goes up by this room's tariff automatically. If a balance is due, use Record payment.</div>
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={!roomId} onClick={() => onSubmit(roomId)}>Add room</Button>
      </div>
    </section>
  );
}
